import { useCallback, useState } from "react";
import apiClient2 from "../api/apiClient2";
import apiPaths from "../api/endpoints";

interface UseRemintAssetReturn {
  /**
   * Refresh one or more signed asset paths.
   * Pass the `answerContent` values (the path portion of the signed URL).
   * Returns an array of refreshed paths in the same order.
   * Falls back per-item to that item's original path on error, so one failed
   * item in a batch doesn't affect the refreshed paths of its siblings.
   */
  remint: (signedPaths: string[]) => Promise<string[]>;
  loading: boolean;
  error: string | null;
}

/**
 * Common hook for refreshing expiring signed-URL tokens.
 *
 * Usage (any component in the app):
 *   const { remint } = useRemintAsset();
 *   const [freshPath] = await remint([item.answerContent]);
 */
const useRemintAsset = (): UseRemintAssetReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remint = useCallback(async (signedPaths: string[]): Promise<string[]> => {
    if (!signedPaths.length) return [];

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient2.post(apiPaths.REMINT_MEDIA, {
        items: signedPaths.map((url) => ({ url })),
      });

      // Response: { results: [{ url } | { error: "invalid" | "too_old" }] }.
      // results is index-aligned with the request's items, so each entry falls back independently.
      const results: Array<{ url?: string; error?: string }> = response.data?.results ?? [];

      return signedPaths.map((original, index) => {
        const result = results[index];
        if (result?.error) {
          console.warn(`[useRemintAsset] item ${index} not renewed: ${result.error}`);
        }
        return result?.url ?? original;
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || "Failed to refresh asset token";
      setError(msg);
      console.error("[useRemintAsset]", msg);
      // Return originals as fallback so the caller can still attempt the action.
      return signedPaths;
    } finally {
      setLoading(false);
    }
  }, []);

  return { remint, loading, error };
};

export default useRemintAsset;
